import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, normalizeStyle as _normalizeStyle, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "query-flow-wrapper relative" }
const _hoisted_2 = {
  key: 0,
  class: "graph-container-wrapper"
}
const _hoisted_3 = {
  key: 0,
  class: "flex items-center justify-center h-full bg-gray-50 rounded-lg"
}
const _hoisted_4 = {
  key: 1,
  class: "graph-scroll-container"
}
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "node-content" }
const _hoisted_7 = { class: "relative w-[95vw] h-[90vh] bg-white rounded-xl p-6 shadow-2xl" }
const _hoisted_8 = { class: "graph-scroll-container h-full" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "node-content" }

import { Background } from '@vue-flow/background'
import { Controls } from '@vue-flow/controls'
import type { Edge, Node } from '@vue-flow/core'
import { Panel, useVueFlow, VueFlow } from '@vue-flow/core'
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

// Import Vue Flow styles
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'

interface QueryNode {
  id: string;
  parentComponent: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'QueryFlowGraph',
  props: {
    queries: {},
    currentQueryId: {}
  },
  emits: ["node-click"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const { onPaneReady, fitView, setCenter, setViewport, getViewport } = useVueFlow()
const router = useRouter()
const isFullScreen = ref(false)
const horizontalSpacing = ref(400)
const verticalSpacing = ref(250)

const nodes = ref<Node[]>([])
const edges = ref<Edge[]>([])

const isValidQuery = computed(() => {
  return props.currentQueryId && props.queries.some(q => q.id === props.currentQueryId);
});

const graphStyle = computed(() => ({
  height: '500px',
  width: '100%',
  minWidth: '400px'
}))

function handleNodeClick(queryId: string) {
  if (!queryId) return;
  
  // First zoom out to show the entire graph
  fitView({ duration: 400, padding: 0.5 });
  
  // After zooming out, wait a bit then navigate
  setTimeout(() => {
    isFullScreen.value = false;
    router.push(`/query/${queryId}`);
  }, 500);
}

const createElements = () => {
  if (!isValidQuery.value) {
    nodes.value = []
    edges.value = []
    return
  }

  const newNodes: Node[] = []
  const newEdges: Edge[] = []
  const processedNodes = new Set()

  // Process nodes starting from current query
  const processNode = (nodeId: string, x: number, y: number, level: number = 0) => {
    if (!nodeId || processedNodes.has(nodeId)) return
    processedNodes.add(nodeId)

    const node = props.queries.find(q => q.id === nodeId)
    if (!node) return

    // Add node with animation class for new nodes
    newNodes.push({
      id: nodeId,
      type: 'custom',
      position: { x, y },
      data: {
        label: nodeId,
        isCurrent: nodeId === props.currentQueryId,
        type: nodeId === props.currentQueryId ? 'current' : 'related',
        level,
        isNew: true // Add this flag for animation
      },
    })

    // Process parent with increased spacing for better visibility
    if (node.parentComponent) {
      const edgeId = `${node.parentComponent}-${nodeId}`
      newEdges.push({
        id: edgeId,
        source: node.parentComponent,
        target: nodeId,
        type: 'smoothstep',
        animated: true,
        style: { stroke: '#15803d', strokeWidth: 2 },
      })

      if (!processedNodes.has(node.parentComponent)) {
        processNode(
          node.parentComponent,
          x,
          y - verticalSpacing.value,
          level - 1
        )
      }
    }

    // Process children with wider spacing
    const children = props.queries.filter(q => q.parentComponent === nodeId)
    const childCount = children.length
    
    children.forEach((child, index) => {
      const offset = (index - (childCount - 1) / 2) * horizontalSpacing.value
      const childX = x + offset
      const childY = y + verticalSpacing.value
      
      processNode(child.id, childX, childY, level + 1)
    })
  }

  // Start processing from current query
  processNode(props.currentQueryId, 0, 0, 0)

  nodes.value = newNodes
  edges.value = newEdges

  // Ensure the graph is properly centered and zoomed
  setTimeout(() => {
    fitView({ 
      duration: 800,
      padding: 0.2,
      includeHiddenNodes: true,
    });
  }, 100);
}

// Watch for changes in currentQueryId or queries
watch([() => props.currentQueryId, () => props.queries], () => {
  createElements()
}, { immediate: true })

// Add a watch for currentQueryId changes
watch(() => props.currentQueryId, (newId, oldId) => {
  if (newId && oldId && newId !== oldId) {
    // When query changes, ensure we show the full graph
    setTimeout(() => {
      fitView({ 
        duration: 800,
        padding: 0.2,
        includeHiddenNodes: true,
      });
    }, 100);
  }
}, { immediate: true });

onMounted(() => {
  if (isValidQuery.value) {
    createElements()
  }
})

onPaneReady(({ fitView }) => {
  if (isValidQuery.value) {
    fitView()
  }
})

const onResetView = () => {
  if (isValidQuery.value) {
    fitView()
  }
}

function toggleFullScreen() {
  isFullScreen.value = !isFullScreen.value
  // Trigger a re-render of the graph
  setTimeout(() => {
    if (isValidQuery.value) {
      fitView()
    }
  }, 100)
}

// Add zoom control functions
const zoomIn = () => {
  const viewport = getViewport()
  const newZoom = Math.min(viewport.zoom * 1.2, 4)
  setViewport({ 
    x: viewport.x, 
    y: viewport.y, 
    zoom: newZoom 
  }, { duration: 300 })
}

const zoomOut = () => {
  const viewport = getViewport()
  const newZoom = Math.max(viewport.zoom / 1.2, 0.2)
  setViewport({ 
    x: viewport.x, 
    y: viewport.y, 
    zoom: newZoom 
  }, { duration: 300 })
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!isFullScreen.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("button", {
            onClick: toggleFullScreen,
            class: "absolute top-4 right-4 z-10 bg-white p-2 rounded-lg shadow-md hover:shadow-lg transition-all"
          }, [...(_cache[2] || (_cache[2] = [
            _createElementVNode("svg", {
              xmlns: "http://www.w3.org/2000/svg",
              class: "h-6 w-6 text-gray-600",
              fill: "none",
              viewBox: "0 0 24 24",
              stroke: "currentColor"
            }, [
              _createElementVNode("path", {
                "stroke-linecap": "round",
                "stroke-linejoin": "round",
                "stroke-width": "2",
                d: "M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"
              })
            ], -1)
          ]))]),
          (!isValidQuery.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[3] || (_cache[3] = [
                _createElementVNode("p", { class: "text-gray-500" }, "No valid query data available", -1)
              ]))]))
            : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createVNode(_unref(VueFlow), {
                  modelValue: nodes.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((nodes).value = $event)),
                  "default-viewport": { zoom: 1.5 },
                  "min-zoom": 0.2,
                  "max-zoom": 4,
                  "fit-view-on-init": true,
                  "node-dimensions-change": true,
                  elevation: 30,
                  style: _normalizeStyle(graphStyle.value),
                  class: "vue-flow-graph",
                  nodes: nodes.value,
                  edges: edges.value,
                  "enable-pan-over-scroll": true,
                  "enable-zoom-over-scroll": true
                }, {
                  "node-custom": _withCtx((nodeProps) => [
                    _createElementVNode("div", {
                      class: _normalizeClass([
                'custom-node',
                nodeProps.data.isCurrent ? 'current' : 'related',
                'transform-gpu'
              ]),
                      onClick: ($event: any) => (handleNodeClick(nodeProps.id))
                    }, [
                      _createElementVNode("div", _hoisted_6, _toDisplayString(nodeProps.data.label), 1)
                    ], 10, _hoisted_5)
                  ]),
                  default: _withCtx(() => [
                    _createElementVNode("div", { class: "viewport-controls absolute top-4 left-4 z-20 bg-white rounded-lg shadow-md p-2 flex flex-col gap-2" }, [
                      _createElementVNode("button", {
                        onClick: zoomIn,
                        class: "zoom-btn hover:bg-gray-100 rounded p-2 transition-colors",
                        title: "Zoom In"
                      }, [...(_cache[4] || (_cache[4] = [
                        _createElementVNode("span", { class: "text-xl font-semibold text-gray-700" }, "+", -1)
                      ]))]),
                      _cache[6] || (_cache[6] = _createElementVNode("div", { class: "h-px bg-gray-200" }, null, -1)),
                      _createElementVNode("button", {
                        onClick: zoomOut,
                        class: "zoom-btn hover:bg-gray-100 rounded p-2 transition-colors",
                        title: "Zoom Out"
                      }, [...(_cache[5] || (_cache[5] = [
                        _createElementVNode("span", { class: "text-xl font-semibold text-gray-700" }, "−", -1)
                      ]))])
                    ]),
                    _createVNode(_unref(Background), {
                      "pattern-color": "#aaa",
                      gap: 12
                    }),
                    _createVNode(_unref(Controls)),
                    _createVNode(_unref(Panel), {
                      position: "top-right",
                      class: "mt-16"
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("button", {
                          onClick: onResetView,
                          class: "reset-view-btn"
                        }, " Reset View ")
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue", "style", "nodes", "edges"])
              ]))
        ]))
      : _createCommentVNode("", true),
    (isFullScreen.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: "fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50",
          onClick: _withModifiers(toggleFullScreen, ["self"])
        }, [
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("button", {
              onClick: toggleFullScreen,
              class: "absolute top-4 right-4 z-10 bg-white p-2 rounded-lg shadow-md hover:shadow-lg transition-all"
            }, [...(_cache[7] || (_cache[7] = [
              _createElementVNode("svg", {
                xmlns: "http://www.w3.org/2000/svg",
                class: "h-6 w-6 text-[#157813]",
                fill: "none",
                viewBox: "0 0 24 24",
                stroke: "currentColor"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  "stroke-width": "2",
                  d: "M6 18L18 6M6 6l12 12"
                })
              ], -1)
            ]))]),
            _createElementVNode("div", _hoisted_8, [
              _createVNode(_unref(VueFlow), {
                modelValue: nodes.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((nodes).value = $event)),
                "default-viewport": { zoom: 1.5 },
                "min-zoom": 0.2,
                "max-zoom": 4,
                "fit-view-on-init": true,
                "node-dimensions-change": true,
                elevation: 30,
                class: "vue-flow-graph h-full",
                nodes: nodes.value,
                edges: edges.value,
                "enable-pan-over-scroll": true,
                "enable-zoom-over-scroll": true
              }, {
                "node-custom": _withCtx((nodeProps) => [
                  _createElementVNode("div", {
                    class: _normalizeClass([
                  'custom-node',
                  nodeProps.data.isCurrent ? 'current' : 'related',
                  'transform-gpu'
                ]),
                    onClick: ($event: any) => (handleNodeClick(nodeProps.id))
                  }, [
                    _createElementVNode("div", _hoisted_10, _toDisplayString(nodeProps.data.label), 1)
                  ], 10, _hoisted_9)
                ]),
                default: _withCtx(() => [
                  _createElementVNode("div", { class: "viewport-controls absolute top-4 left-4 z-20 bg-white rounded-lg shadow-md p-2 flex flex-col gap-2" }, [
                    _createElementVNode("button", {
                      onClick: zoomIn,
                      class: "zoom-btn hover:bg-gray-100 rounded p-2 transition-colors",
                      title: "Zoom In"
                    }, [...(_cache[8] || (_cache[8] = [
                      _createElementVNode("span", { class: "text-xl font-semibold text-gray-700" }, "+", -1)
                    ]))]),
                    _cache[10] || (_cache[10] = _createElementVNode("div", { class: "h-px bg-gray-200" }, null, -1)),
                    _createElementVNode("button", {
                      onClick: zoomOut,
                      class: "zoom-btn hover:bg-gray-100 rounded p-2 transition-colors",
                      title: "Zoom Out"
                    }, [...(_cache[9] || (_cache[9] = [
                      _createElementVNode("span", { class: "text-xl font-semibold text-gray-700" }, "−", -1)
                    ]))])
                  ]),
                  _createVNode(_unref(Background), {
                    "pattern-color": "#aaa",
                    gap: 12
                  }),
                  _createVNode(_unref(Controls)),
                  _createVNode(_unref(Panel), {
                    position: "top-right",
                    class: "mt-16"
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("button", {
                        onClick: onResetView,
                        class: "reset-view-btn"
                      }, " Reset View ")
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["modelValue", "nodes", "edges"])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})